import React from "react"
import { BackIcon, Box, Button, ForwardIcon, Typography, useTheme } from "@carrotfertility/carotene-core"
import { Form, FormAutocomplete, FormButton, FormTextField } from "@carrotfertility/carotene-core-x"
import { UsaStatesAndDc } from "@internal/constants"
import { FormattedMessage, useIntl } from "react-intl"
import { InsuranceProviderField } from "./InsuranceProviderField"
import type { CoverageDetails } from "./types"
import type { InsuranceInputs } from "../../insuranceTypes"
type Props = {
  inputs?: InsuranceInputs | null
  onExit?: () => void
  memberReady: boolean
  onSubmit: (inputs: InsuranceInputs) => Promise<void>
}

export function InsuranceDetailsStep({ inputs, onExit, memberReady, onSubmit }: Props): JSX.Element {
  const intl = useIntl()
  const stateOptions = UsaStatesAndDc.map((state) => ({ value: state.id, label: intl.formatMessage(state.label) }))
  const details: CoverageDetails = {
    state: stateOptions.find((state) => state.value === inputs?.state) ?? null,
    insuranceProvider: inputs ? { value: inputs.payerId, label: inputs.payerName } : null,
    insuranceMemberId: inputs?.memberId ?? ""
  }
  const required = intl.formatMessage({ defaultMessage: "Required" })
  const theme = useTheme()

  return (
    <Box sx={{ inlineSize: "100%" }}>
      <Typography variant="h3" component="h1" marginBottom={theme.spacing(theme.tokens.spacing.sm)}>
        <FormattedMessage defaultMessage="Let's check your coverage" />
      </Typography>
      <Typography marginBottom={theme.spacing(theme.tokens.spacing.lg)}>
        <FormattedMessage defaultMessage="Enter your insurance details and we'll estimate what you'll pay for this visit." />
      </Typography>
      {!memberReady && (
        <Typography role="alert">
          <FormattedMessage defaultMessage="Your legal name, date of birth, and visit configuration are needed to check coverage. Please review your profile or contact your Carrot Companion." />
        </Typography>
      )}
      <Box sx={{ inlineSize: "100%" }}>
        <Form<CoverageDetails>
          defaultValues={details}
          onSubmit={(values) => {
            if (values.state && values.insuranceProvider)
              void onSubmit({
                state: String(values.state.value),
                payerId: String(values.insuranceProvider.value),
                payerName: String(values.insuranceProvider.label),
                memberId: values.insuranceMemberId.trim()
              })
          }}
        >
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(theme.tokens.spacing.lg)
            })}
          >
            <FormAutocomplete
              name="state"
              label={intl.formatMessage({ defaultMessage: "Confirm your location (State)" })}
              options={stateOptions}
              fullWidth
              controllerProps={{ rules: { required } }}
            />
            <InsuranceProviderField />
            <FormTextField
              name="insuranceMemberId"
              label={intl.formatMessage({ defaultMessage: "Member ID" })}
              placeholder={intl.formatMessage({ defaultMessage: "e.g., XJ4 820 551" })}
              helperText={intl.formatMessage({ defaultMessage: "The ID number printed on your insurance card." })}
              fullWidth
              registerOptions={{ required, validate: (value: string) => Boolean(value.trim()) || required }}
            />
            <Box
              sx={(theme) => ({
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                gap: theme.spacing(theme.tokens.spacing.md)
              })}
            >
              <Button type="button" variant="text" color="secondary" startIcon={<BackIcon />} onClick={onExit}>
                <FormattedMessage defaultMessage="Back" />
              </Button>
              <FormButton
                disabled={!memberReady}
                type="submit"
                variant="outlined"
                color="secondary"
                endIcon={<ForwardIcon />}
              >
                <FormattedMessage defaultMessage="Check coverage" />
              </FormButton>
            </Box>
          </Box>
        </Form>
      </Box>
    </Box>
  )
}
